import { Typography } from '@material-ui/core'
import { attributes, define, Events, memberOf } from '@type-r/models'
import { useIO, useModel, useEvent } from "@type-r/react"
import { AddFilter, asCurrency, asTimelessDate, Flex, Loading, Page, SelectSortBy, SmallTile, TilesList, FiltersList, CompactTabs, Tab, useLinkedQueryParam } from 'lqx-react'
import * as React from 'react'
import { CompanyModel, globalStore, PlatformCompany } from 'api'
import { UnresolvedPayment, UnresolvedPaymentFilters } from '../../api/unresolvedPayment'
import { PaymentDetails } from './details'
import { useMemo } from "react";
import { SmallChip } from '../../../../../../common/inmatch'
import { matchString } from 'api/common'

const sortOptions = {
    statementDate: 'Statement Date',
    valueDate: 'Value Date',
    debtorName: 'Debtor Name',
    creditorName: 'Creditor Name',
    amount: 'Transaction Amount',
};

@define
class State extends attributes({
    payments : [ UnresolvedPayment ],
    companies : [ PlatformCompany ],
    selected : memberOf( 'payments', UnresolvedPayment ),
    filter : UnresolvedPaymentFilters,
    category : 'all',
    sortBy : {
        attr: 'statementDate',
        asc : false
    },

    ioCounter : 0
}){
    async fetch(){   
        this.companies = globalStore.platformCompanies;
        
        await Promise.all([
            this.payments.fetch(),
        ]);

        this.transaction( () => {
            if (this.payments) {
                this.filter.initializeOptions(this.payments);
            }

            if( this.selected && !this.payments.get( this.selected ) ){
                this.selected = null;
            }
        })

        return this;
    }
}

export const ResolvePaymentsPage = () =>{
    const state = useModel( State );

    const $valueDateQueryParam = useLinkedQueryParam("valueDate");
    
    const isReady = useIO(
        () => state.fetch(),
        [ state.ioCounter ]
    );

    useEvent( Events, 'bankTransactionSummary:refresh', () => state.ioCounter++ );

    const counts = useMemo(
        () => {
          return {
            ar: state.payments.filter(x => x.filter(state.filter, 'ar')).length,
            nonAr: state.payments.filter(x => x.filter(state.filter, 'nonAr')).length
          }},
        [
          state.payments["_changeToken"],
          state.filter["_changeToken"]
        ]
      );

    const sortedAndFiltered = useMemo( () => {
        const { attr, asc } = state.sortBy;
        return state.payments
            .filter(payment => payment.filter(state.filter, state.category))
            .filter(p =>!$valueDateQueryParam || !$valueDateQueryParam.value || matchString(asTimelessDate(p.valueDate), $valueDateQueryParam.value.toString() )
        )
            .sort(UnresolvedPayment.comparator(attr as any, asc));
    },
    [ state.payments['_changeToken'], state.sortBy['_changeToken'], state.filter['_changeToken'], state.category, $valueDateQueryParam.value ]);

    return isReady ? (
        <Page
            menuWidth={500}
            left={
                <>
                    <CompactTabs label="Payment type" $value={state.$.category}>
                        <Tab value="all">All ({counts.ar + counts.nonAr})</Tab>
                        <Tab value="ar">AR ({counts.ar})</Tab>
                        <Tab value="nonAr">Non-AR ({counts.nonAr})</Tab>
                    </CompactTabs>
                    <FiltersList filter={state.filter} />
                    <AddFilter
                        filter={state.filter}
                        filterAllPlaceholder="Filter by..."
                    />
                    <Flex row>
                        <SelectSortBy $attr={ state.sortBy.$.attr } $asc={ state.sortBy.$.asc } options={ sortOptions } />
                    </Flex>

                    {sortedAndFiltered.length ? (
                        <TilesList
                            items={sortedAndFiltered}
                            itemSize={64}
                            renderer={PaymentTile(state.$.selected)}
                            selectedItem={state.selected}
                        />
                    ) : (
                        <Flex center stretch>
                           <Typography variant="h4">No results found.</Typography>
                        </Flex>
                    )}
                    </>
            }
        >
            { state.selected ? 
                <PaymentDetails key={state.selected.cid} payment={ state.selected } companies={ state.companies }/>
            :
                <Flex center stretch>
                    <Typography variant="h4">Select a payment</Typography>
                </Flex>
            }
        </Page>
    )
        : 
    <Loading />
}

const PaymentTile = $selected => ({ data, style, index }) => {
    const payment: UnresolvedPayment = data.items[index];

    return (
      <div style={style}>
        <SmallTile
          color={'green'}
          isSelected={$selected.value === payment}
          onClick={e => {
            $selected.set(payment);
          }}
          footer={
            <>
              <div>{payment.customer.name}</div>
              <Flex grow />  
            </>
          }
        >
          {asCurrency(payment.amount)}
          <Flex grow />
          { payment.payorAgentId ? <SmallChip>POBO</SmallChip> : null }
          {payment.age > 2 ? <SmallChip>{payment.age} days</SmallChip> : null}
          {asTimelessDate(payment.valueDate)}
        </SmallTile>
      </div>
    );
  };